/**
 * AppICEMAP — ErrorBoundary
 * Custom element: `<error-boundary>`
 *
 * Catches unhandled errors at the window level and displays a friendly
 * fallback message with a retry button. Does NOT wrap children — instead
 * listens globally and shows/hides itself.
 *
 * Usage:
 *   <error-boundary></error-boundary>
 *
 * When a critical error is caught, the component shows a dark-themed
 * fallback message. The user can dismiss or reload.
 */

export class ErrorBoundary extends HTMLElement {
	constructor() {
		super();
		this._hasError = false;
		this._errorMessage = "";
	}

	connectedCallback() {
		this.style.display = "none";
		this._render();

		// Global error handler
		this._onError = (event) => {
			const { message, filename, lineno, colno } = event;
			console.error(
				"[ErrorBoundary] Unhandled error:",
				message,
				filename,
				lineno,
				colno,
			);
			this._showError(message || "Error desconocido");
		};

		this._onUnhandledRejection = (event) => {
			const reason =
				event.reason?.message || event.reason || "Promesa rechazada";
			console.error("[ErrorBoundary] Unhandled rejection:", event.reason);
			// Don't show UI for every rejection — only truly unhandled ones
			if (!event.defaultPrevented) {
				this._showError(String(reason).slice(0, 200));
			}
		};

		window.addEventListener("error", this._onError);
		window.addEventListener("unhandledrejection", this._onUnhandledRejection);
	}

	disconnectedCallback() {
		window.removeEventListener("error", this._onError);
		window.removeEventListener(
			"unhandledrejection",
			this._onUnhandledRejection,
		);
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_showError(message) {
		if (this._hasError) return; // Already showing
		this._hasError = true;
		this._errorMessage = message;
		this._render();
		this.style.display = "block";
	}

	_hide() {
		this._hasError = false;
		this._errorMessage = "";
		this.style.display = "none";
		this._render();
	}

	_render() {
		if (!this._hasError) {
			this.innerHTML = "";
			return;
		}

		this.innerHTML = `
			<style>
				:host {
					position: fixed; inset: 0; z-index: 9999;
					display: flex; align-items: center; justify-content: center;
					background: var(--color-bg, #161826);
					font-family: var(--font-body, system-ui, -apple-system, sans-serif);
				}
				.eb {
					text-align: center;
					padding: var(--space-8, 32px) var(--space-6, 24px);
					max-width: 320px;
				}
				.eb__icon {
					font-size: 3rem;
					color: var(--color-neutral-500, #9397ab);
					margin-bottom: var(--space-4, 16px);
					display: block;
				}
				.eb__title {
					font-size: var(--text-lg, 1.25rem);
					font-weight: var(--font-heading-weight, 500);
					color: var(--color-text, #e9e9ed);
					margin: 0 0 var(--space-2, 8px);
				}
				.eb__message {
					font-size: var(--text-sm, 0.8125rem);
					color: var(--color-neutral-400, #b2b6ca);
					margin: 0 0 var(--space-6, 24px);
					line-height: var(--leading-sm, 1.45);
					word-break: break-word;
				}
				.eb__actions {
					display: flex; gap: var(--space-3, 12px);
					justify-content: center; flex-wrap: wrap;
				}
				.eb__btn {
					padding: var(--space-3, 12px) var(--space-6, 24px);
					border: none; border-radius: var(--radius-full, 9999px);
					font-size: var(--text-base, 0.9375rem);
					font-weight: 500; cursor: pointer;
					transition: opacity var(--duration-fast, 150ms);
				}
				.eb__btn--primary {
					background: var(--color-accent, #9184d9);
					color: #fff;
				}
				.eb__btn--primary:hover { opacity: 0.85; }
				.eb__btn--secondary {
					background: var(--color-neutral-800, #3f424d);
					color: var(--color-text, #e9e9ed);
				}
				.eb__btn--secondary:hover { background: var(--color-neutral-700, #595d6c); }
			</style>
			<div class="eb">
				<i class="ph ph-smiley-sad eb__icon"></i>
				<h2 class="eb__title">Algo salió mal</h2>
				<p class="eb__message">${this._escapeHtml(this._errorMessage)}</p>
				<div class="eb__actions">
					<button class="eb__btn eb__btn--secondary" data-action="dismiss">Cerrar</button>
					<button class="eb__btn eb__btn--primary" data-action="reload">Reintentar</button>
				</div>
			</div>
		`;

		this.querySelector('[data-action="reload"]')?.addEventListener(
			"click",
			() => {
				window.location.reload();
			},
		);

		this.querySelector('[data-action="dismiss"]')?.addEventListener(
			"click",
			() => {
				this._hide();
			},
		);
	}

	_escapeHtml(str) {
		const div = document.createElement("div");
		div.textContent = str;
		return div.innerHTML;
	}
}

if (!customElements.get("error-boundary")) {
	customElements.define("error-boundary", ErrorBoundary);
}
